import { useState, useContext } from 'react';
import Flatpickr from 'react-flatpickr';
import { AppContext } from '../../../../../common/context';
import { getMonths } from '../../../../../utils';
import Table from '../../../../../common/filter-table/Table';
import { EventModel } from '../model/EventModel';
import { EventTypesEnum } from '../../../../../models/inconsistencies-validation/eventTypesEnum';
import React from 'react';
import Loading from '../../../../../common/filter-table/Loading';
import { useGetEventsByUser } from '../hooks/useGetEventsByUser';

export const EventsTable = () => {
    const { lang } = useContext(AppContext);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    const { data, isLoading, error } = useGetEventsByUser(startDate, endDate);
    
    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const handlerChangeDate = (dates: Date[]) => {
        if (!dates || dates.length < 2) return;
        
        const start = formatDate(dates[0]);
        const end = formatDate(dates[1]);

        setStartDate(start);
        setEndDate(end);
    };

    const groupEventsByDate = (events: EventModel[]): Record<string, EventModel[]> => {
        return events.reduce((acc, event) => {
            const key = formatDate(new Date(event.eventDate));
            
            acc[key] = acc[key] || [];
            acc[key].push(event);
            
            return acc;
        }, {} as Record<string, EventModel[]>);
    };

    const groupEventsByLocation = (events: EventModel[]) => {
        return events.reduce((acc, event) => {
            const location = event.ubicationName;
            
            acc[location] = acc[location] || [];
            acc[location].push(event);
            
            return acc;
        }, {} as Record<string, EventModel[]>);
    };

    const eventTypeLabels: Record<number, string> = {
        [EventTypesEnum.Arrival]: 'Entrada',
        [EventTypesEnum.Departure]: 'Salida',
        [EventTypesEnum.Invalid]: 'Inválido'
    }

    const groupedEvents = groupEventsByDate(data ?? []);

    return (
        <>
            <div className='col-12 col-md-6 ps-2'>
                <h2 className="text-primary2 fw-bold mb-2">Marcaciones</h2>
                <p className="text-muted mb-1">
                    Consulta las marcaciones registradas en el sistema según el rango de fechas seleccionado.
                </p>
                <label className="form-label fw-semibold" htmlFor="picker-date">
                    Fecha Inicial / Fecha Final
                </label>
                <div className='date__picker custom w-75' >
                    <Flatpickr
                        lang='es'
                        title={`${startDate} to ${endDate}`}
                        id='picker-date'
                        className='form-control picker text-black select__control'
                        options={{
                            mode: 'range',
                            dateFormat: 'Y-m-d',
                            locale: {
                                months: getMonths(lang),
                            },
                        }}
                        onChange={handlerChangeDate}
                        placeholder='YYYY-MM-DD'
                    />
                    <label htmlFor='picker-date'>
                        <i className='mdi mdi-calendar-blank fs-4 text-muted'></i>
                    </label>
                </div>
            </div>

            <br />
            <div className='table-responsive'>
                <Table
                    columns={[
                        { isSortable: true, name: 'EventDate', title: 'Fecha del Evento' },
                        { isSortable: true, name: 'HourDate', title: 'Hora del Evento' },
                        { isSortable: true, name: 'UbicationName', title: 'Ubicación' },
                        { isSortable: true, name: 'EventTypeId', title: 'Tipo de Evento' },
                    ]}
                >
                {isLoading ? (
                    <Loading hasSkeleton={true} skeletonType="table" />
                ) : !data || data.length === 0 ? (
                    <tr>
                        <td colSpan={4} className="text-center text-muted py-3">
                            No hay eventos registrados en este rango de fechas.
                        </td>
                    </tr>
                ) : (
                    Object.entries(groupedEvents)
                        .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
                        .map(([date, dayEvents]) => {
                            const groupedByLocation = groupEventsByLocation(dayEvents);
                            
                            return (
                                <React.Fragment key={date}>
                                    {
                                    Object.entries(groupedByLocation).map(([location, locationEvents], locIndex) =>
                                        locationEvents
                                            .sort((a, b) =>new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime() ) 
                                            .map((event, eventIndex) => (
                                                <tr key={event.eventId} className="thin-row align-middle">
                                                    {locIndex === 0 && eventIndex === 0 && (
                                                        <td rowSpan={dayEvents.length} className="fw-bold text-primary bg-light align-middle">
                                                            {new Date(date).toLocaleDateString('es-ES')}
                                                        </td>
                                                    )} 
                                                    <td>
                                                        {new Date(event.eventDate).toLocaleTimeString('es-ES', {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        })}
                                                    </td>
                                                    {eventIndex === 0 && (
                                                        <td rowSpan={locationEvents.length} className="fw-semibold">
                                                            {location}
                                                        </td>
                                                    )}
                                                    <td>{eventTypeLabels[event.eventTypeId]}</td>
                                                </tr>
                                            ))
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </Table>
            </div>
        </>
    );
};